import React, { createContext, useCallback, useEffect, useRef, Suspense, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Outlet } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import i18n from '@lib/i18n';
import mockData from '../mocks/org_data.json';

import {
  ApiUtil,
  AutoLogout,
  ConfirmationModal,
  ChakraUIProvider,
  HeaderMenuWrapper,
  KeyboardUtil,
  ProgressSpinner,
  Sidebar,
  UrlUtil,
  WindowHelper,
  SystemNotifications,
  useBreakpointRef,
} from '@everfi/reactifi';
import { useTheme } from '@everfi/design-system';

import { useLocalStorage } from '../lib/useLocalStorage';
import environment from '../environment';
import { useTokenStore } from '../authentication/TokenStore';
import { getHomeRoomOldUrl } from '../common/HomeRoomUtils';
import { getLanguageLabels } from '../teacher/common/languageLabels';
const {
  ADMINIFI_URL,
  CERTIFICATE_SERVICE_URL,
  CONTENT_SERVICE_URL,
  COMPLIANCE_URL,
  DASHIFI_URL,
  ENGAGE_URL,
  EVALUFI_URL,
  FOUNDRY_KNOWLEDGE_BASE_URL,
  HOMEROOM,
  HOMEROOM_PLATFORM_URL,
  HOMEROOM_ADMIN_URL,
  NEXT_URL,
  NOTIFI_URL,
  apiBasePath,
} = environment;

const MENU_ENVIRONMENT = {
  MENU_DOMAIN_FOR_ADMINIFI: ADMINIFI_URL,
  MENU_DOMAIN_FOR_CONTENT_SERVICE: CONTENT_SERVICE_URL,
  MENU_DOMAIN_FOR_CERTIFICATE_SERVICE: CERTIFICATE_SERVICE_URL,
  MENU_DOMAIN_FOR_COMPLIANCE: COMPLIANCE_URL,
  MENU_DOMAIN_FOR_EVALUFI: EVALUFI_URL,
  MENU_DOMAIN_FOR_ENGAGE: ENGAGE_URL,
  MENU_DOMAIN_FOR_FRONT_END: DASHIFI_URL,
  MENU_DOMAIN_FOR_NOTIFI: NOTIFI_URL,
  MENU_DOMAIN_FOR_NEXT: NEXT_URL,
  MENU_DOMAIN_FOR_HOMEROOM: HOMEROOM_PLATFORM_URL,
  FOUNDRY_KNOWLEDGE_BASE_URL,
  HOMEROOM_ADMIN_URL,
};

export const AppContext = createContext({});

const ORG_BRANDING_STYLE_CLASS = '.org-branding-styles';

// login code borrowed from https://github.com/kdhttps/appauth-react
// and related blog: https://dev.to/kdhttps/appauth-js-integration-in-react-1m3e
const Main = ({ app, hideImpersonation = false, portal, sidebar, systemNotifications }) => {
  const tokenStore = useTokenStore();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isHomeroom = app === HOMEROOM;
  const mockApis = portal === 'next_learner';
  const contentEl = useRef(null);

  const userDataKey = `${slug ? `${slug}-user` : `admin`}-data`;
  const [showModalNavigate, setShowModalNavigate] = useState(false);
  const [orgData, setOrgData] = useLocalStorage(`${slug}-org-data`);
  const [userData, setUserData] = useLocalStorage(userDataKey);
  const [languages, setLanguages] = useLocalStorage('languages', ['en']);
  const BREAKPOINTS = Object.freeze([1080]);
  const BREAKPOINT_VALUES = Object.freeze(['', i18n.t('New View')]);

  const { breakpointValue } = useBreakpointRef({
    breakpoints: BREAKPOINTS,
    breakpointValues: BREAKPOINT_VALUES,
    direction: '',
  });

  const currentRuleSetParam = searchParams.get('current_rule_set_name');
  let [currentRuleSetName, setCurrentRuleSetName] = useLocalStorage('current_rule_set_name', currentRuleSetParam);
  if (currentRuleSetParam && currentRuleSetParam !== currentRuleSetName) {
    setCurrentRuleSetName(currentRuleSetParam);
    currentRuleSetName = currentRuleSetParam;
  }

  const setActiveRuleSet = useCallback(
    data => {
      if (slug && data?.rule_sets) {
        data.rule_sets.forEach(ruleSet => {
          ruleSet.active = ruleSet.name === currentRuleSetName;
          ruleSet.interstitial_redirect = `${ADMINIFI_URL}/${slug}/cportal/interstitial_redirect?current_rule_set_name=${ruleSet.name}`;
        });
      }
    },
    [currentRuleSetName, slug]
  );

  setActiveRuleSet(userData);

  useEffect(() => {
    const portalClass = {
      fit: 'admin',
      customer: 'cportal',
      learner: 'learner',
      teacher: 'teacher',
    };
    document.body.classList.add(portalClass[portal]);
    if (!hideImpersonation && userData?.impersonator_id) {
      document.body.classList.add('impersonating');
    }
    if (!sidebar) {
      // TODO: see if we can change the styles in everfi-material so we don't have to have this class on the body
      document.body.classList.add('no-sidebar');
    }
    return () => {
      if (userData?.impersonator_id) {
        document.body.classList.remove('impersonating');
      }
      if (!sidebar) {
        document.body.classList.remove('no-sidebar');
      }
    };
  }, [portal, sidebar, userData?.impersonator_id, hideImpersonation]);

  useEffect(() => {
    const urlWithoutLang = window.location.pathname.replace(`${i18n.language}/`, '');

    if (isHomeroom) {
      async function fetchSchoolLocales() {
        const localesArray = await ApiUtil.loadExternalData(
          `${environment.apiBasePath.HOMEROOM}/current_school/locales`,
          tokenStore.currentToken
        );

        if (localesArray.length) {
          const schoolLanguages = localesArray.reduce((result, locale) => {
            const localeFormat = i18n.options.languages.includes(locale.id) ? locale.id : locale.language_abbreviation;
            result[localeFormat] = {
              ...locale,
              native_name: getLanguageLabels([locale.id])[0],
              translation_url: `https://${window.location.hostname}/${localeFormat}${urlWithoutLang}`,
            };
            return result;
          }, {});

          setLanguages(schoolLanguages);
        }
      }

      fetchSchoolLocales();
      return;
    }

    if (!slug) return;

    async function fetchOrgData() {
      try {
        const data = await ApiUtil.loadExternalData(
          `${apiBasePath.ADMINIFI}/organizations/by_slug/${slug}`,
          tokenStore.currentToken
        );
        setOrgData(data);
        const languagesArray = await ApiUtil.loadExternalData(
          `${apiBasePath.ADMINIFI}/languages`,
          tokenStore.currentToken
        );
        const langList = languagesArray.reduce((result, lang) => {
          result[lang.code] = {
            ...lang,
            translation_url: `/${lang.code.replace('_', '-')}${urlWithoutLang}`,
          };
          return result;
        }, {});
        setLanguages(langList);
      } catch (error) {
        if ([404, 403].includes(error.response.status)) {
          tokenStore.clearToken();
          WindowHelper.WindowHelperWrapper(window).redirect(`${ADMINIFI_URL}/${slug}/sign_out`);
        } else {
          throw error;
        }
      }
    }
    async function mockOrgData() {
      try {
        setOrgData(mockData);
        const languagesArray = mockData.languages;
        const langList = languagesArray.reduce((result, lang) => {
          result[lang.code] = {
            ...lang,
            translation_url: `/${lang.code.replace('_', '-')}${urlWithoutLang}`,
          };
          return result;
        }, {});
        setLanguages(langList);
      } catch (error) {
        throw error;
      }
    }

    mockApis ? mockOrgData() : fetchOrgData();
  }, [tokenStore, setOrgData, slug, app, setLanguages, isHomeroom, mockApis]);

  useEffect(() => {
    if (!orgData?.id) return;

    const head = document.getElementsByTagName('head')?.item(0);
    if (!head) return;

    const prevTag = head.getElementsByClassName(ORG_BRANDING_STYLE_CLASS)?.item(0);
    if (prevTag) return;

    const cssTag = document.createElement('link');
    cssTag.href = `${ADMINIFI_URL}/brand_style_guides.css?organization_id=${orgData?.id}`;
    cssTag.type = 'text/css';
    cssTag.rel = 'stylesheet';
    cssTag.classList.add(ORG_BRANDING_STYLE_CLASS);

    head.appendChild(cssTag);
  }, [orgData]);

  useEffect(() => {
    async function fetchUserData() {
      try {
        const context = slug ? 'users' : 'administrators';
        let url = `${apiBasePath.ADMINIFI}/${context}/me`;

        if (isHomeroom) {
          url = `${HOMEROOM_PLATFORM_URL}/api/v3/me`;
        }
        const data = await ApiUtil.loadExternalData(url, tokenStore.currentToken);
        if (isHomeroom && !Array.isArray(data.rule_sets)) {
          data.rule_sets = [{ name: data.rule_sets, active: true, label: i18n.t('Role'), role: data.rule_sets }];
        }
        setActiveRuleSet(data);
        setUserData({ ...data, homeroom_user: isHomeroom });
        datadogRum.setUser({ id: data.id });
      } catch (error) {
        if ([404, 403].includes(error.response.status)) {
          tokenStore.clearToken();
          WindowHelper.WindowHelperWrapper(window).redirect(
            slug ? `${ADMINIFI_URL}/${slug}/sign_out` : `${ADMINIFI_URL}/admin/session`
          );
        } else {
          throw error;
        }
      }
    }
    !mockApis && fetchUserData();
  }, [tokenStore, app, currentRuleSetName, setActiveRuleSet, setUserData, slug, userDataKey, isHomeroom, mockApis]);

  const contextValues = {
    accessToken: tokenStore.currentToken,
    currentRuleSet: userData?.rule_sets?.find(ruleSet => ruleSet.active) || (userData?.rule_sets || [])[0],
    currentRuleSetName,
    organization: orgData,
    user: userData,
  };

  const menuSlugs = {
    fit: {
      sidebar: 'everfi_admin_left',
      topbar: 'everfi_admin_navigation',
    },
    customer: {
      sidebar: 'customer_admin_left',
      topbar: 'cportal_user_navigation',
    },
    learner: {
      topbar: 'learner_navigation_menu',
    },
    teacher: {
      sidebar: 'homeroom_teacher_left',
      topbar: 'homeroom_teacher_navigation',
    },
    next_learner: {
      topbar: 'next_learner_menu',
    },
  };

  const viewOptions = isHomeroom
    ? [
        { label: i18n.t('New View'), description: i18n.t('We’re trying something new out!'), value: 'new-view' },
        { label: i18n.t('Classic View'), description: i18n.t('Back to what you know'), value: 'old-view' },
      ]
    : null;

  const onSkipClick = e => {
    e.preventDefault();
    contentEl.current.focus();
  };

  const handleExitImpersonation = () => {
    tokenStore.clearToken();
  };

  const onViewChange = async selectedOption => {
    if (selectedOption.value === 'old-view') {
      setShowModalNavigate(true);
    }
  };

  const onConfirmNavigation = async () => {
    const baseUrl = userData?.impersonator_id ? HOMEROOM_ADMIN_URL : HOMEROOM_PLATFORM_URL;
    const oldUrl = getHomeRoomOldUrl(window.location.pathname);
    window.location.href = `${baseUrl}/${oldUrl}?try_classic_experience=true`;
  };

  const hasUserData = !!userData;
  const hasOrgData = app !== HOMEROOM && !!orgData;
  const canRender = hasUserData || hasOrgData;

  let themeLibrary = 'chakraUiTheme';
  if (isHomeroom) {
    themeLibrary = 'k12';
  }

  const { activeThemeDict } = useTheme(themeLibrary);

  return (
    <AppContext.Provider value={contextValues}>
      <ChakraUIProvider theme={activeThemeDict}>
        <div>
          <button
            aria-label="Skip to main content"
            className="sr-only-focusable"
            id="skipLink"
            onClick={onSkipClick}
            onKeyDown={KeyboardUtil.onExecuteKeyDown(onSkipClick)}
            tabIndex="0"
          >
            Skip to main content
          </button>
          <HeaderMenuWrapper
            appendLanguage={false}
            currentRuleSet={contextValues.currentRuleSet}
            currentRuleSetName={contextValues.currentRuleSetName}
            environment={MENU_ENVIRONMENT}
            hideImpersonation={hideImpersonation}
            isDashiFi={true}
            isHomeroom={isHomeroom}
            languages={languages}
            onExitImpersonation={handleExitImpersonation}
            organization={orgData}
            selectedLanguage={localStorage?.getItem('i18nextLng')}
            slug={menuSlugs[portal]?.topbar}
            theme={activeThemeDict}
            user={userData}
            useSearchParams={useSearchParams}
          />
          <ConfirmationModal
            show={showModalNavigate}
            title={i18n.t('Taking you to a new place!')}
            children={i18n.t('Click “Let’s Go” to navigate back to the classic Teacher Dashboard!')}
            confirmButtonText={i18n.t('Let’s Go')}
            onCancel={() => setShowModalNavigate(false)}
            onConfirm={onConfirmNavigation}
            modalClass="modal-rounded"
          />
          {sidebar && (
            <Sidebar
              currentRuleSet={contextValues.currentRuleSet}
              currentRuleSetName={contextValues.currentRuleSetName}
              environment={MENU_ENVIRONMENT}
              organization={orgData}
              slug={menuSlugs[portal]?.sidebar}
              theme={activeThemeDict}
              useNavigate={useNavigate}
              user={userData}
              useSearchParams={useSearchParams}
              options={viewOptions}
              optionsFilterLabel={breakpointValue}
              onChange={onViewChange}
            />
          )}

          {!mockApis && (
            <>
              <AutoLogout
                sessionUrl={`${app === HOMEROOM ? HOMEROOM_PLATFORM_URL : ADMINIFI_URL}/oauth/token/info`}
                authToken={tokenStore.currentToken}
                useOauthInfo={true}
                clearToken={tokenStore.clearToken}
              />
            </>
          )}

          {/*
        TODO: Other Elements that need to be added somewhere in here
        - Inactive Org banner
        - tag manager and related alert message
        - Role Picker Modal?
        - Footer?
        */}

          {systemNotifications && (
            <SystemNotifications adminifiUrl={ADMINIFI_URL} userId={userData?.id} authToken={tokenStore.currentToken} />
          )}
          <main id="main" className="p2">
            <div id="content">
              <button tabIndex="-1" id="content-focus" className="sr-only" ref={contentEl} role="status">
                Main content
              </button>
              <Suspense fallback={<ProgressSpinner pageLoader={true} renderAsLegacy={false} />}>
                {canRender && <Outlet />}
              </Suspense>
            </div>
          </main>
        </div>
      </ChakraUIProvider>
    </AppContext.Provider>
  );
};

Main.displayName = 'Main';

export default Main;
